import { useRef, useState } from "react";
import { MessageCircle, Columns2 } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { waLink } from "@/lib/wa";
import { trackEvent, trackWhatsApp } from "@/lib/analytics";

const SEGMENTOS = ["Residencial", "Empresarial", "Automotivo"];

const PELICULAS = [
  { key: "sem-pelicula", label: "Sem película", swatch: "#E8F4FD" },
  { key: "fume-claro", label: "Fumê claro", swatch: "#93A6B9" },
  { key: "fume-medio", label: "Fumê médio", swatch: "#5B6B7D" },
  { key: "fume-escuro", label: "Fumê escuro", swatch: "#232E3C" },
  { key: "seguranca-incolor", label: "Segurança incolor", swatch: "#CFE9F5" },
  { key: "amarelo", label: "Amarelo", swatch: "#FACC15" },
  { key: "espelhado", label: "Espelhado", swatch: "linear-gradient(135deg,#F1F5F9,#94A3B8,#E2E8F0)" },
  { key: "jateado", label: "Jateado", swatch: "#F8FAFC" },
  { key: "blackout", label: "Blackout", swatch: "#0B0B0B" },
];

function overlayStyle(key) {
  switch (key) {
    case "sem-pelicula": return {};
    case "fume-claro": return { background: "rgba(10,25,47,0.28)" };
    case "fume-medio": return { background: "rgba(10,25,47,0.52)" };
    case "fume-escuro": return { background: "rgba(10,25,47,0.78)" };
    case "seguranca-incolor": return { background: "rgba(255,255,255,0.05)", boxShadow: "inset 0 0 0 3px rgba(0,180,216,0.4)" };
    case "amarelo": return { background: "rgba(250,204,21,0.48)" };
    case "espelhado": return { background: "linear-gradient(135deg, rgba(241,245,249,0.94), rgba(148,163,184,0.9) 45%, rgba(226,232,240,0.92))", backdropFilter: "blur(1px)" };
    case "jateado": return { background: "rgba(255,255,255,0.45)", backdropFilter: "blur(8px)" };
    case "blackout": return { background: "#0B0B0B" };
    default: return {};
  }
}

function Scene({ segmento, pelicula, compare, split, onSplit }) {
  const frameRef = useRef(null);
  const dragging = useRef(false);

  const handlePointer = (e) => {
    if (!compare) return;
    if (e.type === "pointerdown") dragging.current = true;
    if (!dragging.current) return;
    const rect = frameRef.current.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    onSplit(Math.min(96, Math.max(4, pct)));
  };
  const stop = () => { dragging.current = false; };

  return (
    <div
      ref={frameRef}
      onPointerDown={handlePointer}
      onPointerMove={handlePointer}
      onPointerUp={stop}
      onPointerLeave={stop}
      style={{ touchAction: compare ? "none" : "auto" }}
      className={`relative aspect-[4/3] overflow-hidden rounded-2xl border-[10px] border-navy shadow-2xl ${compare ? "cursor-ew-resize select-none" : ""}`}
    >
      <div className={`absolute inset-0 ${
        segmento === "Automotivo"
          ? "bg-gradient-to-b from-sky-300 via-sky-100 to-zinc-300"
          : segmento === "Empresarial"
            ? "bg-gradient-to-b from-sky-200 via-sky-100 to-slate-200"
            : "bg-gradient-to-b from-sky-300 via-sky-100 to-emerald-50"
      }`} />
      <div className="absolute right-8 top-6 h-12 w-12 rounded-full bg-yellow-300 shadow-[0_0_40px_rgba(253,224,71,0.8)] md:right-12 md:top-8 md:h-16 md:w-16" aria-hidden="true" />
      {segmento === "Automotivo" ? (
        <>
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-zinc-500" aria-hidden="true" />
          <div className="absolute inset-x-0 bottom-[15%] h-1.5 opacity-80" style={{ background: "repeating-linear-gradient(90deg,#fff 0 26px,transparent 26px 52px)" }} aria-hidden="true" />
        </>
      ) : (
        <div className="absolute inset-x-0 bottom-0 flex h-2/5 items-end justify-center gap-2 px-6" aria-hidden="true">
          {[38, 60, 45, 72, 52, 64, 40].map((h, i) => (
            <div key={i} style={{ height: `${h}%` }}
                 className={`w-full rounded-t-sm ${segmento === "Empresarial" ? "bg-slate-400/70" : "bg-emerald-900/25"}`} />
          ))}
        </div>
      )}
      <div
        className={`absolute inset-0 ${compare ? "" : "transition-all duration-500"}`}
        style={{ ...overlayStyle(pelicula), ...(compare ? { clipPath: `inset(0 0 0 ${split}%)` } : {}) }}
        data-testid="simulador-vidro"
      />
      {compare && (
        <>
          <span className="absolute left-3 top-12 rounded-full bg-white/85 px-3 py-1 font-display text-[11px] font-bold text-navy" data-testid="simulador-antes">Antes</span>
          <span className="absolute right-3 top-12 rounded-full bg-navy/85 px-3 py-1 font-display text-[11px] font-bold text-white" data-testid="simulador-depois">Depois</span>
          <div className="absolute inset-y-0 z-10" style={{ left: `${split}%` }} data-testid="simulador-barra" aria-hidden="true">
            <div className="absolute inset-y-0 -ml-px w-0.5 bg-white shadow-[0_0_10px_rgba(0,0,0,0.4)]" />
            <div className="absolute top-1/2 -ml-5 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-navy shadow-lg">
              <Columns2 size={18} />
            </div>
          </div>
        </>
      )}
      <div className="absolute left-3 top-3 rounded-full bg-navy/80 px-4 py-1.5 font-display text-xs font-bold text-white backdrop-blur" data-testid="simulador-selecao">
        {segmento} · {PELICULAS.find((p) => p.key === pelicula)?.label}
      </div>
    </div>
  );
}

export function Simulador() {
  const [segmento, setSegmento] = useState("Residencial");
  const [pelicula, setPelicula] = useState("fume-claro");
  const [compare, setCompare] = useState(false);
  const [split, setSplit] = useState(50);
  const peliculaLabel = PELICULAS.find((p) => p.key === pelicula)?.label || pelicula;

  const msg = `Olá! Usei o simulador do site da Bella Film RJ. Segmento: ${segmento}. Película: ${peliculaLabel}. Gostaria de solicitar um orçamento.`;

  return (
    <section className="order-2 bg-white py-14 md:order-none md:py-32" data-testid="simulador-section" aria-labelledby="simulador-titulo">
      <div className="mx-auto max-w-7xl px-5 md:px-12">
        <Reveal>
          <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-aqua">Simulador</p>
          <h2 id="simulador-titulo" className="mt-3 max-w-2xl font-display text-xl font-bold tracking-tight text-navy md:mt-4 md:text-5xl">
            Veja como o vidro pode ficar
          </h2>
          <p className="mt-3 max-w-xl text-sm text-navy/65 md:text-base">
            Escolha o segmento e a película para visualizar o efeito no vidro.
          </p>
        </Reveal>
        <div className="mt-8 grid items-start gap-8 md:mt-12 md:grid-cols-2 md:gap-14">
          <Reveal>
            <p className="font-display text-sm font-bold text-navy">1. Segmento</p>
            <div className="mt-3 flex flex-wrap gap-2" role="tablist" aria-label="Segmento da simulação">
              {SEGMENTOS.map((s) => (
                <button key={s} role="tab" aria-selected={segmento === s} data-testid={`simulador-tab-${s.toLowerCase()}`}
                        onClick={() => { setSegmento(s); trackEvent("simulador_segmento", { segmento: s }); }}
                        className={`rounded-full px-5 py-2.5 font-display text-xs font-semibold transition-colors md:text-sm ${
                          segmento === s ? "bg-navy text-white" : "bg-mist text-navy ring-1 ring-navy/15 hover:ring-aqua"}`}>
                  {s}
                </button>
              ))}
            </div>
            <p className="mt-7 font-display text-sm font-bold text-navy">2. Película</p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {PELICULAS.map((p) => (
                <button key={p.key} data-testid={`simulador-pelicula-${p.key}`} aria-pressed={pelicula === p.key}
                        onClick={() => { setPelicula(p.key); trackEvent("simulador_pelicula", { pelicula: p.label, segmento }); }}
                        className={`flex flex-col items-center gap-2 rounded-xl border-2 px-2 py-3 text-center transition-all ${
                          pelicula === p.key ? "border-aqua bg-aqua-soft/50" : "border-navy/10 bg-white hover:border-aqua/50"}`}>
                  <span className="h-8 w-8 rounded-full border border-navy/15" style={{ background: p.swatch }} aria-hidden="true" />
                  <span className="font-display text-[11px] font-semibold leading-tight text-navy md:text-xs">{p.label}</span>
                </button>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button type="button" data-testid="simulador-comparar-toggle" aria-pressed={compare}
                      onClick={() => setCompare(!compare)}
                      className={`inline-flex items-center gap-2 rounded-full border-2 px-5 py-2.5 font-display text-xs font-bold transition-colors md:text-sm ${
                        compare ? "border-aqua bg-aqua text-white" : "border-navy/25 text-navy hover:border-aqua"}`}>
                <Columns2 size={15} /> {compare ? "Fechar comparador" : "Comparar Antes × Depois"}
              </button>
              <a href={waLink(msg)} target="_blank" rel="noopener noreferrer" data-testid="simulador-cta"
                 onClick={() => trackWhatsApp("simulador", `${segmento} - ${peliculaLabel}`)}
                 className="inline-flex items-center gap-2 rounded-full bg-aqua px-6 py-3 font-display text-xs font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-aqua-dark hover:shadow-lg md:px-7 md:py-3.5 md:text-sm">
                <MessageCircle size={16} /> Solicitar orçamento desta película
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Scene segmento={segmento} pelicula={pelicula} compare={compare} split={split} onSplit={setSplit} />
            <p className="mt-4 text-xs leading-relaxed text-navy/55" data-testid="simulador-aviso">
              {compare ? "Arraste a barra para comparar o vidro sem película (antes) e com a película escolhida (depois). " : ""}
              Simulação ilustrativa. O resultado pode variar conforme o vidro, a iluminação do ambiente e a película escolhida.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
